import React, { useEffect, useRef, useState } from "react";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { fontWeightClass } from "~/assets/styles/classes/InputClasses";
import { useTranslation } from "react-i18next";
import { VM_CPU_CORE_MAX } from "~/Utils/Consts";

interface DualThumbSliderProps {
  id: string;
  min?: number;
  max?: number;
  step?: number;
  minValue: number;
  maxValue: number;
  onChange: (values: { minValue: number; maxValue: number }) => void;
  label?: string;
  required?: boolean;
  fontWeight?: "bold" | "normal" | "extrabold";
  errorLabel?: string;
  unity?: string;
  testId?: string;
}

export const DualThumbSlider: React.FC<DualThumbSliderProps> = ({
  id,
  min = 1,
  max = VM_CPU_CORE_MAX,
  step = 1,
  minValue,
  maxValue,
  onChange,
  label = "Slider",
  required = false,
  fontWeight = "bold",
  errorLabel,
  unity,
  testId,
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<"min" | "max" | null>(null);
  const [touched, setTouched] = useState(false);
  const { t } = useTranslation();

  /** Convert value to percentage for slider styling */
  const valueToPercent = (value: number) => ((value - min) / (max - min)) * 100;

  /** Handle dragging logic */
  const handleMouseMove = (e: MouseEvent) => {
    if (!trackRef.current || !dragging) return;

    const rect = trackRef.current.getBoundingClientRect();
    const position = ((e.clientX - rect.left) / rect.width) * (max - min) + min;
    const rounded = Math.round(position / step) * step;

    if (dragging === "min") {
      const newMin = Math.min(Math.max(min, rounded), maxValue);
      onChange({ minValue: newMin, maxValue });
    }

    if (dragging === "max") {
      const newMax = Math.max(Math.min(max, rounded), minValue);
      onChange({ minValue, maxValue: newMax });
    }
  };

  const handleMouseUp = () => setDragging(null);

  /** Attach / detach mouse events when dragging */
  useEffect(() => {
    if (dragging) {
      globalThis.addEventListener("mousemove", handleMouseMove);
      globalThis.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      globalThis.removeEventListener("mousemove", handleMouseMove);
      globalThis.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging, minValue, maxValue]);

  /** Required error logic */
  const showRequiredError = required && !touched;
  const requiredMessage = errorLabel || "This field is required";

  return (
    <div className="relative w-full">
      {/* Label */}
      <label
        htmlFor={id}
        className={`inline-block text-sm ${fontWeightClass(fontWeight)} ${
          showRequiredError ? "text-error" : ""
        }`}
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>

      {/* Hidden input for native validation */}
      <input
        type="hidden"
        name={id}
        value={touched ? `${minValue}-${maxValue}` : ""}
        required={required}
        data-test-id={testId}
      />

      {/* Slider track */}
      <div ref={trackRef} className="relative mt-4 h-6 w-full">
        {/* Base track */}
        <div className="absolute top-1/2 h-2 w-full -translate-y-1/2 rounded-lg bg-gray-200" />

        {/* Active range */}
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 rounded-lg bg-[#D073BE]"
          style={{
            left: `${valueToPercent(minValue)}%`,
            width: `${valueToPercent(maxValue) - valueToPercent(minValue)}%`,
          }}
        />

        {/* Min thumb */}
        <button
          type="button"
          className="absolute top-1/2 z-10 h-5 w-5 -translate-y-1/2 cursor-pointer rounded-full border-2 border-red-500 bg-white"
          style={{
            left: `calc(${valueToPercent(minValue)}% - 10px)`,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setTouched(true);
            setDragging("min");
          }}
        />

        {/* Max thumb */}
        <button
          type="button"
          className="absolute top-1/2 z-10 h-5 w-5 -translate-y-1/2 cursor-pointer rounded-full border-2 border-red-500 bg-white"
          style={{
            left: `calc(${valueToPercent(maxValue)}% - 10px)`,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            setTouched(true);
            setDragging("max");
          }}
        />
      </div>

      {/* Display values */}
      <div className="mt-2 flex justify-between text-sm text-gray-700">
        <span className="h-6 font-arial text-base font-normal leading-6">
          {t("Min")}: <strong>{minValue}</strong> {unity}
        </span>
        <span className="h-6 font-arial text-base font-normal leading-6">
          {t("Max")}: <strong>{maxValue}</strong> {unity}
        </span>
      </div>

      {/* Required / Error message */}
      {showRequiredError && (
        <div className="mt-1 flex items-center gap-1 text-error">
          <HighlightOffRoundedIcon fontSize="small" />
          <span className="text-sm leading-[18px]">{requiredMessage}</span>
        </div>
      )}
    </div>
  );
};
